/**
 * Access-token management for Bearer providers (plan §6).
 *
 * One token per provider, shared through the same Redis the caches use (`rs:tok:{id}`) with an
 * in-process mirror. Flow: use the cached token while it has > 60 s left; at 60 s before its
 * JWT `exp` renew it with the stored refresh_token (when the provider configures a refresh
 * endpoint); if the refresh fails do a full re-login with the credentials from env
 * (SOCIAL_PROV_<ID>_EMAIL / _PASSWORD). A failed re-login is remembered for 30 s and thrown to the
 * caller, which records it as a provider failure, so repeat failures open the breaker (./health.ts).
 *
 * Renewal never runs inside a request's 2.5 s attempt (Kicksta/Buzzoid logins measured 2.2-4.1 s):
 * a token inside its last 60 s is renewed in the background while the old one still serves, and with
 * no usable token at all the login runs in the background and the attempt is SKIPPED (ProviderSkip:
 * not a failure) so the request moves on to the next lane.
 *
 * 🔴 Browser+edge-safe: fetch + atob only (no Buffer, no node:*). Tokens live in Redis only,
 *    never in logs.
 */
import { redisClient } from '../redis';
import { bounded, fireAndForget, getCredentials, ProviderSkip, runInBackground, type Provider } from './registry';

/** Renew this long before the token expires. */
export const REFRESH_LEAD_MS = 60_000;
/** Logins run in the background, so they get more than a request attempt (measured 2.2-4.1 s). */
const LOGIN_TIMEOUT_MS = 8_000;
/** After a failed login, serve the error for this long instead of hammering the provider. */
const LOGIN_RETRY_MS = 30_000;
const LOCK_TTL_S = 10;

interface TokenRecord {
	accessToken: string;
	refreshToken?: string;
	/** ms epoch */
	exp: number;
}

const mirror = new Map<string, TokenRecord>();
const inflight = new Map<string, Promise<string>>();
const loginErrors = new Map<string, { at: number; message: string }>();

const tokenKey = (id: string) => `rs:tok:${id}`;
const lockKey = (id: string) => `rs:toklock:${id}`;

/** `exp` (ms epoch) of a JWT, or null when it isn't one. Pure. */
export function jwtExpiryMs(token: string): number | null {
	try {
		const part = token.split('.')[1];
		if (!part) return null;
		const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
		const json = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
		const exp = JSON.parse(json)?.exp;
		return typeof exp === 'number' && exp > 0 ? exp * 1000 : null;
	} catch {
		return null;
	}
}

function needsRenewal(rec: TokenRecord, now: number): boolean {
	return rec.exp - now <= REFRESH_LEAD_MS;
}

function toRecord(
	provider: Provider,
	parsed: { accessToken: string; refreshToken?: string; expiresInS?: number },
	now: number
): TokenRecord {
	const fromJwt = jwtExpiryMs(parsed.accessToken);
	const exp = fromJwt ?? now + (parsed.expiresInS ?? provider.tokenAuth!.fallbackTtlS) * 1000;
	return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken, exp };
}

async function readShared(id: string): Promise<TokenRecord | null> {
	try {
		const raw = await bounded(redisClient.get(tokenKey(id)), 400);
		const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (v && typeof (v as any).accessToken === 'string' && typeof (v as any).exp === 'number') return v as TokenRecord;
	} catch { /* Redis down / corrupt: act as if absent */ }
	return null;
}

/** Persist the token. The write is AWAITED (a background login has its own 8 s) because a token written only
 *  fire-and-forget never survived the end of the edge response: every new isolate re-logged in and, while that
 *  login ran, both lanes answered "token login in progress", so lookups went 5 s cold. */
async function writeShared(id: string, rec: TokenRecord, now: number): Promise<void> {
	// Keep the entry as long as its refresh token may still be useful (≥ 1 h, ≤ 7 d).
	const ttl = Math.min(7 * 24 * 3600, Math.max(3600, Math.ceil((rec.exp - now) / 1000) + 3600));
	try {
		await bounded(redisClient.set(tokenKey(id), JSON.stringify(rec), { ex: ttl }), 1_000);
	} catch (error: any) {
		console.error(`[router/tokens] ${id}: token write failed (${error?.message || error})`);
	}
}

async function postJson(url: string, spec: { headers?: Record<string, string>; body: string }, outerSignal?: AbortSignal): Promise<any> {
	const ctrl = new AbortController();
	const timer = setTimeout(() => ctrl.abort(), LOGIN_TIMEOUT_MS);
	const onAbort = () => ctrl.abort();
	outerSignal?.addEventListener('abort', onAbort, { once: true });
	try {
		const res = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...spec.headers },
			body: spec.body,
			signal: ctrl.signal,
		});
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		return await res.json();
	} finally {
		clearTimeout(timer);
		outerSignal?.removeEventListener('abort', onAbort);
	}
}

async function renew(provider: Provider, previous: TokenRecord | null, signal?: AbortSignal): Promise<string> {
	const auth = provider.tokenAuth;
	if (!auth) throw new Error(`${provider.id}: no tokenAuth configured`);
	const now = Date.now();

	// 1. refresh_token grant (only when the provider configures one and we hold a refresh token)
	if (previous?.refreshToken && auth.refresh) {
		try {
			const json = await postJson(auth.refresh.url, auth.refresh.body(previous.refreshToken), signal);
			const parsed = auth.parseTokens(json);
			if (parsed?.accessToken) {
				const rec = toRecord(provider, { ...parsed, refreshToken: parsed.refreshToken ?? previous.refreshToken }, now);
				mirror.set(provider.id, rec);
				await writeShared(provider.id, rec, now);
				return rec.accessToken;
			}
		} catch (error: any) {
			console.warn(`[router/tokens] ${provider.id}: refresh failed (${error?.message || error}), re-logging in`);
		}
	}

	// 2. full re-login
	const creds = getCredentials(provider.id);
	if (!creds) throw new Error(`${provider.id}: no credentials`);
	const json = await postJson(auth.loginUrl, auth.loginBody(creds.email, creds.password), signal);
	const parsed = auth.parseTokens(json);
	if (!parsed?.accessToken) throw new Error(`${provider.id}: login response had no token`);
	const rec = toRecord(provider, parsed, now);
	mirror.set(provider.id, rec);
	await writeShared(provider.id, rec, now);
	return rec.accessToken;
}

/**
 * The access token to use for a request, without ever waiting for a login. Throws ProviderSkip while a
 * login is in flight (not a failure), a plain Error when the last login failed (a failure sample).
 */
export async function getToken(provider: Provider, _signal?: AbortSignal): Promise<string> {
	const now = Date.now();
	const local = mirror.get(provider.id) ?? null;
	if (local && local.exp > now) {
		if (needsRenewal(local, now)) renewInBackground(provider);
		return local.accessToken;
	}
	const shared = await readShared(provider.id);
	if (shared && shared.exp > Date.now()) {
		mirror.set(provider.id, shared);
		if (needsRenewal(shared, Date.now())) renewInBackground(provider);
		return shared.accessToken;
	}
	const failed = loginErrors.get(provider.id);
	if (failed && now - failed.at < LOGIN_RETRY_MS) throw new Error(`${provider.id}: login failed: ${failed.message}`);
	renewInBackground(provider);
	throw new ProviderSkip(`${provider.id}: token login in progress`);
}

function renewInBackground(provider: Provider): void {
	if (inflight.has(provider.id)) return;
	runInBackground(
		ensureToken(provider).then(
			() => { loginErrors.delete(provider.id); },
			(error: any) => {
				if (error instanceof ProviderSkip) return; // another isolate holds the login lock: not a failure
				loginErrors.set(provider.id, { at: Date.now(), message: String(error?.message || error).slice(0, 120) });
				console.error(`[router/tokens] ${provider.id}: renewal failed:`, error?.message || error);
			}
		)
	);
}

/** Renew (refresh_token, else login) and return a valid token — awaits the network. Single-flight per provider. */
export async function ensureToken(provider: Provider, signal?: AbortSignal): Promise<string> {
	const now = Date.now();
	let rec = mirror.get(provider.id) ?? null;
	if (rec && !needsRenewal(rec, now)) return rec.accessToken;

	const pending = inflight.get(provider.id);
	if (pending) return pending;

	const run = (async () => {
		// Another isolate may already have renewed: look at the shared copy first.
		const shared = await readShared(provider.id);
		if (shared && (!rec || shared.exp > rec.exp)) {
			rec = shared;
			mirror.set(provider.id, shared);
			if (!needsRenewal(shared, Date.now())) return shared.accessToken;
		}
		// One renewer across isolates when Redis is up; if the lock is held, wait briefly for its result.
		let haveLock = true;
		try {
			haveLock = !!(await bounded(redisClient.set(lockKey(provider.id), '1', { nx: true, ex: LOCK_TTL_S })));
		} catch { /* Redis down: renew locally */ }
		if (!haveLock) {
			// Someone else is logging in (2-4 s). Never log in alongside them: use the shared token while it is
			// still valid, otherwise step aside — the next request retries once their login lands.
			const after = await readShared(provider.id);
			if (after && after.exp > Date.now()) {
				mirror.set(provider.id, after);
				return after.accessToken;
			}
			throw new ProviderSkip(`${provider.id}: token login in progress elsewhere`);
		}
		return await renew(provider, rec, signal);
	})();
	inflight.set(provider.id, run);
	try {
		return await run;
	} finally {
		inflight.delete(provider.id);
	}
}

/** The provider rejected the token (401): drop it everywhere so the next call re-logs in. */
export async function invalidateToken(provider: Provider, rejected?: string): Promise<void> {
	// Only the token the provider actually rejected may be cleared: a 401 for an old token must not
	// throw away a newer one that another request or isolate has already put in place.
	const rec = mirror.get(provider.id);
	// Keep the refresh token (the next call tries it first) but force immediate renewal.
	if (rec && (rejected === undefined || rec.accessToken === rejected)) mirror.set(provider.id, { ...rec, exp: 0 });
	try {
		const shared = await readShared(provider.id);
		if (shared && (rejected === undefined || shared.accessToken === rejected))
			await bounded(redisClient.set(tokenKey(provider.id), JSON.stringify({ ...shared, exp: 0 }), { ex: 3600 }));
	} catch { /* best effort */ }
}

/** Test hook. */
export function __resetTokens(): void {
	mirror.clear();
	inflight.clear();
	loginErrors.clear();
}
